import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Plus, Trash2 } from "lucide-react";
import { QuoteTable } from "@/components/quotes/QuoteTable";
import { QuoteQuickView } from "@/components/quotes/QuoteQuickView";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: "",
    items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
    tax: 0,
    notes: "",
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchQuotes();
    fetchCustomers();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuote) {
        await axios.put(`${API}/quotes/${editingQuote.id}`, formData);
        toast.success("Quote updated successfully");
      } else {
        await axios.post(`${API}/quotes`, formData);
        toast.success("Quote created successfully");
      }
      setOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quote");
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      customer_id: quote.customer_id,
      items: quote.items.map((it) => ({ ...it })),
      tax: quote.tax,
      notes: quote.notes || "",
      valid_until: quote.valid_until
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success("Quote deleted successfully");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      await axios.post(`${API}/quotes/${id}/submit`);
      toast.success("Quote submitted for approval");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit quote");
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/quotes/${id}/approve`);
      toast.success("Quote approved and sent to customer");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve quote");
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Enter rejection reason:");
    if (!reason) return;
    try {
      await axios.post(`${API}/quotes/${id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Quote rejected");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to reject quote");
    }
  };

  const handleConvertToInvoice = async (id) => {
    try {
      await axios.post(`${API}/quotes/${id}/convert-to-invoice`);
      toast.success("Quote converted to invoice");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert quote");
    }
  };

  const downloadPDF = async (id, quoteNumber) => {
    try {
      const response = await axios.get(`${API}/quotes/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${quoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const emailQuote = async (id) => {
    try {
      await axios.post(`${API}/quotes/${id}/email`);
      toast.success("Quote sent to customer successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send quote");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
      tax: 0,
      notes: "",
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    setEditingQuote(null);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = formData.items.map((it) => ({ ...it }));
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    return subtotal + (formData.tax || 0);
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-purple-600 to-purple-700 text-white border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Quotes</h1>
              <p className="text-purple-100">Manage customer quotes and approvals</p>
            </div>
            <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetForm(); setOpen(isOpen); }}>
              <DialogTrigger asChild>
                <Button
                  onClick={resetForm}
                  className={`bg-white text-purple-600 hover:bg-purple-50 ${can("quotes.create") ? "" : "hidden"}`}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Quote
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingQuote ? "Edit Quote" : "Create New Quote"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="customer">Customer *</Label>
                    <CustomerCombobox
                      customers={customers}
                      value={formData.customer_id}
                      onChange={(value) => setFormData({ ...formData, customer_id: value })}
                      onCreated={(c) => setCustomers((prev) => [...prev, c])}
                      allowCreate
                      testId="customer-select"
                    />
                  </div>

                  <div>
                    <Label>Items *</Label>
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          required
                        />
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                          className="w-24"
                          required
                        />
                        <Input
                          type="number"
                          placeholder="Rate"
                          value={item.rate}
                          onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value))}
                          className="w-32"
                          required
                        />
                        <Input value={`$${item.amount.toFixed(2)}`} readOnly className="w-32" />
                        {formData.items.length > 1 && (
                          <Button type="button" variant="destructive" size="icon" onClick={() => removeItem(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addItem} className="mt-2">
                      <Plus className="w-4 h-4 mr-2" /> Add Item
                    </Button>
                  </div>

                  <div>
                    <Label htmlFor="tax">Tax Amount</Label>
                    <Input
                      id="tax"
                      type="number"
                      step="0.01"
                      value={formData.tax}
                      onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="valid_until">Valid Until *</Label>
                    <Input
                      id="valid_until"
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes or terms..."
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-blue-600">${calculateTotal().toFixed(2)}</span>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { resetForm(); setOpen(false); }} data-testid="quote-form-cancel">
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingQuote ? "Update Quote" : "Create Quote"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <>
          <QuoteTable
            quotes={quotes}
            customers={customers}
            currentUser={currentUser}
            actions={{ downloadPDF, emailQuote, onEdit: handleEdit, onDelete: handleDelete, submitForApproval: handleSubmitForApproval, approve: handleApprove, reject: handleReject, convertToInvoice: handleConvertToInvoice, onView: setViewQuote }}
          />
          {viewQuote && (
            <QuoteQuickView
              quote={quotes.find((q) => q.id === viewQuote.id) || viewQuote}
              onClose={() => setViewQuote(null)}
              currentUser={currentUser}
              actions={{ downloadPDF, emailQuote, onEdit: handleEdit, submitForApproval: handleSubmitForApproval, approve: handleApprove, reject: handleReject, convertToInvoice: handleConvertToInvoice }}
            />
          )}
        </>
      )}
    </div>
  );
}
